import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFacilities } from '../hooks/useFacilities';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import {
  TrendingUp, Calendar, IndianRupee, Banknote, Smartphone, Clock,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { Sparkline, SBAvatar } from '../components/primitives';
import BookingDrawer from '../components/BookingDrawer';
import { todayIST, nowIST, shiftDayIST, fmtDateIST } from '../../utils/ist';

// ── Date helpers ───────────────────────────────────────────────────────────────
const TODAY = todayIST();

function dateLabel(dateStr) {
  if (dateStr === TODAY) return 'Today';
  if (dateStr === shiftDayIST(TODAY, -1)) return 'Yesterday';
  if (dateStr === shiftDayIST(TODAY, +1)) return 'Tomorrow';
  return new Date(dateStr + 'T12:00:00+05:30').toLocaleDateString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata',
  });
}

function periodBounds(preset, customFrom, customTo) {
  const now = nowIST(); // local fields = IST wall clock
  const y = now.getFullYear();
  const m = now.getMonth();
  const fmt = d => d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const iso = (yr, mo, day) => fmt(new Date(yr, mo, day));

  switch (preset) {
    case 'today':
      return { start: TODAY, end: TODAY };

    case 'week': {
      const d = new Date(now);
      const dow = d.getDay();
      d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
      const end = new Date(d); end.setDate(d.getDate() + 6);
      return { start: fmt(d), end: fmt(end) };
    }

    case 'all':
      return { start: null, end: null };

    case 'month':
      return { start: iso(y, m, 1), end: iso(y, m + 1, 0) };

    case 'lastMonth':
      return { start: iso(y, m - 1, 1), end: iso(y, m, 0) };

    case 'quarter': {
      const q = Math.floor(m / 3);
      return { start: iso(y, q * 3, 1), end: iso(y, q * 3 + 3, 0) };
    }

    case 'year':
      return { start: iso(y, 0, 1), end: iso(y, 11, 31) };

    case 'ytd':
      return { start: iso(y, 0, 1), end: iso(y, 11, 31) };

    case 'custom':
      return { start: customFrom || null, end: customTo || null };

    default:
      return { start: null, end: null };
  }
}

const LOCATION_COLORS = ['#facc15', '#60a5fa', '#c084fc', '#fb923c', '#f472b6', '#22d3ee', '#a3e635'];
// Single consistent color for the aggregate "All total" bar across every chart,
// chosen so it never collides with a location color.
const TOTAL_COLOR = '#64748b';

// Build a stable location_id → color map so each location keeps the same color in
// every chart, regardless of bar order, zero values, or location filtering.
function nameInitials(name = '') {
  const p = name.trim().split(/\s+/).filter(Boolean);
  return (p.length >= 2 ? p[0][0] + p[1][0] : name.slice(0, 2)).toUpperCase();
}
function nameAvc(name = '') {
  let h = 0; for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 5;
  return h;
}

function buildLocationColorMap(facilities) {
  const map = {};
  [...facilities]
    .sort((a, b) => a.location_id - b.location_id)
    .forEach((f, i) => { map[f.location_id] = LOCATION_COLORS[i % LOCATION_COLORS.length]; });
  return map;
}

const PRESETS = [
  { key: 'today',     label: 'Today' },
  { key: 'week',      label: 'This Week' },
  { key: 'month',     label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'quarter',   label: 'This Quarter' },
  { key: 'ytd',       label: 'YTD' },
  { key: 'year',      label: 'This Year' },
  { key: 'all',       label: 'All Time' },
  { key: 'custom',    label: 'Custom' },
];

function periodLabel(preset, customFrom, customTo) {
  const p = PRESETS.find(p => p.key === preset);
  if (preset === 'custom') {
    if (customFrom && customTo) return `${customFrom} → ${customTo}`;
    if (customFrom) return `From ${customFrom}`;
    if (customTo) return `Until ${customTo}`;
    return 'Custom';
  }
  return p?.label || 'All Time';
}

// ── Shared location bar chart ──────────────────────────────────────────────────
function LocationBarChart({ title, pLabel, loading, data, dataKey, valueFormatter, totalLabel, colorMap = {} }) {
  if (!data || data.length === 0) return null;
  const colorFor = (l, i) => colorMap[l?.location_id] ?? LOCATION_COLORS[i % LOCATION_COLORS.length];
  const total = data.reduce((s, d) => s + (d[dataKey] ?? 0), 0);
  const chartData = data.length > 1
    ? [...data, { location_name: 'All Locations', [dataKey]: total, isTotal: true }]
    : data;
  return (
    <div className="sb-card" style={{ opacity: loading ? 0.5 : 1, transition: 'opacity .15s' }}>
      <div className="sb-card-head">
        <div>
          <div className="sb-card-lbl">{pLabel}</div>
          <h3>{title}</h3>
        </div>
        <span className="sb-mono sb-num" style={{ fontSize: 14, fontWeight: 500 }}>
          {valueFormatter(total)}
        </span>
      </div>
      <div style={{ padding: 18 }}>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={chartData}>
            <XAxis dataKey="location_name" stroke="var(--line)" tick={{ fill: 'var(--ink-3)', fontSize: 11 }} />
            <YAxis stroke="var(--line)" tick={{ fill: 'var(--ink-3)', fontSize: 11 }} tickFormatter={valueFormatter} />
            <Tooltip
              contentStyle={{ background: 'var(--surface)', border: '1px solid var(--line)', color: 'var(--ink)', fontSize: 12 }}
              formatter={v => [valueFormatter(v), title]}
            />
            <Bar dataKey={dataKey} radius={[4, 4, 0, 0]}>
              {chartData.map((entry, i) => (
                <Cell key={i} fill={entry.isTotal ? TOTAL_COLOR : colorFor(entry, i)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginTop: 10 }}>
          {data.map((l, i) => (
            <span key={l.location_id} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--ink-3)' }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: colorFor(l, i), flexShrink: 0 }} />
              {l.location_name}
            </span>
          ))}
          {data.length > 1 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--ink-3)' }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: TOTAL_COLOR, flexShrink: 0 }} />
              {totalLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────
function SBStatCard({ label, value, sub, isCcy, suffix, trend }) {
  return (
    <div className="sb-card">
      <div className="sb-stat">
        <div className="sb-stat-lbl">{label}</div>
        <div className="sb-stat-val sb-num">
          {isCcy && <span className="sb-stat-ccy">₹</span>}
          {value ?? '—'}
          {suffix && <span className="sb-stat-ccy" style={{ marginLeft: 2 }}>{suffix}</span>}
        </div>
        {sub && <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{sub}</div>}
        {trend && trend.length > 1 && <Sparkline data={trend} color="var(--brand)" />}
      </div>
    </div>
  );
}

function SBStatCardSkeleton() {
  return (
    <div className="sb-card" style={{ padding: '22px 22px 20px' }}>
      <div style={{ height: 11, background: 'var(--line)', borderRadius: 4, width: 80, marginBottom: 12 }} />
      <div style={{ height: 36, background: 'var(--line)', borderRadius: 4, width: 120 }} />
    </div>
  );
}

// A large, clickable stat card (keyboard-accessible) used on the location-admin
// dashboard to deep-link into the calendar / filtered bookings list.
function StatNavCard({ label, value, hint, warn = false, onClick }) {
  const activate = () => onClick && onClick();
  return (
    <div
      className="sb-card"
      role="button"
      tabIndex={0}
      onClick={activate}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); } }}
      style={{ padding: '22px 22px 18px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 12, minHeight: 132 }}
    >
      <div className="sb-card-lbl">{label}</div>
      <div
        className="sb-stat-val sb-num"
        style={{ fontSize: 44, lineHeight: 1, color: warn && value > 0 ? 'var(--warn-fg)' : 'var(--ink)' }}
      >
        {value}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--brand)', marginTop: 'auto' }}>{hint}</div>
    </div>
  );
}

function DateNav({ date, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <button onClick={() => onChange(shiftDayIST(date, -1))} className="sb-btn ghost icon"><ChevronLeft size={14} /></button>
      <input
        type="date"
        value={date}
        onChange={e => e.target.value && onChange(e.target.value)}
        className="sb-form-input"
        style={{ width: 'auto', padding: '6px 10px', fontSize: 12 }}
      />
      <button onClick={() => onChange(shiftDayIST(date, 1))} className="sb-btn ghost icon"><ChevronRight size={14} /></button>
      {date !== TODAY && (
        <button onClick={() => onChange(TODAY)} className="sb-btn ghost sm" style={{ color: 'var(--brand)' }}>Today</button>
      )}
    </div>
  );
}

function PeriodSelector({ preset, customFrom, customTo, onPresetChange, onCustomChange }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
      <div className="sb-chipset">
        {PRESETS.map(p => (
          <div
            key={p.key}
            className={`sb-chip ${preset === p.key ? 'active' : ''}`}
            onClick={() => onPresetChange(p.key)}
          >
            {p.label}
          </div>
        ))}
      </div>
      {preset === 'custom' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="date" value={customFrom}
            onChange={e => onCustomChange(e.target.value, customTo)}
            className="sb-form-input" style={{ width: 'auto', padding: '6px 10px', fontSize: 12 }}
          />
          <span style={{ color: 'var(--ink-4)', fontSize: 13 }}>→</span>
          <input
            type="date" value={customTo}
            onChange={e => onCustomChange(customFrom, e.target.value)}
            className="sb-form-input" style={{ width: 'auto', padding: '6px 10px', fontSize: 12 }}
          />
        </div>
      )}
    </div>
  );
}

function LocationFilter({ facilities, selectedLoc, onChange }) {
  if (facilities.length <= 1) return null;
  return (
    <div className="sb-chipset">
      <div className={`sb-chip ${selectedLoc === null ? 'active' : ''}`} onClick={() => onChange(null)}>
        All locations
      </div>
      {facilities.map(f => (
        <div
          key={f.location_id}
          className={`sb-chip ${selectedLoc === f.location_id ? 'active' : ''}`}
          onClick={() => onChange(f.location_id)}
        >
          {f.name}
        </div>
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const { user, api } = useAuth();
  const navigate = useNavigate();

  // Period for top stat cards
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  // Stats data
  const [stats, setStats] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(true);
  const [periodLoading, setPeriodLoading] = useState(false);

  // Location admin
  const [adminDate, setAdminDate] = useState(TODAY);
  const [adminStats, setAdminStats] = useState(null);
  const [adminLoading, setAdminLoading] = useState(true);

  // Global location filter — applies to ALL sections (stat cards, chart, collections, utilization)
  const { facilities } = useFacilities();
  const [selectedLoc, setSelectedLoc] = useState(null); // null = All
  const [todayBookings, setTodayBookings] = useState([]);
  const [drawerBooking, setDrawerBooking] = useState(null);

  const hasLoadedRef = useRef(false);

  // Today's bookings (owner) — drives Today's schedule + Pending payments sections
  useEffect(() => {
    if (user?.role !== 'owner') return;
    const params = { date: TODAY, page_size: 200 };
    if (selectedLoc) params.location_id = selectedLoc;
    api.get('/api/bookings/', { params })
      .then(res => setTodayBookings(res.data.bookings || []))
      .catch(() => { /* non-fatal */ });
  }, [api, user?.role, selectedLoc]);

  // ── Owner fetch ──────────────────────────────────────────────────────────────
  const fetchOwner = useCallback((options = {}) => {
    const {
      pStart = null,
      pEnd = null,
      locId = selectedLoc,
    } = options;
    const isFirst = !hasLoadedRef.current;

    if (isFirst) setGlobalLoading(true);
    else setPeriodLoading(true);

    const params = {};
    if (pStart)  params.period_start = pStart;
    if (pEnd)    params.period_end   = pEnd;
    if (locId)   params.location_id  = locId;

    api.get('/api/dashboard/stats', { params })
      .then(res => {
        setStats(res.data);
        hasLoadedRef.current = true;
      })
      .catch(err => {
        if (err?.response?.status !== 401 && err?.response?.status !== 403)
          toast.error('Failed to load dashboard');
      })
      .finally(() => {
        setGlobalLoading(false);
        setPeriodLoading(false);
      });
  }, [api, selectedLoc]);

  // ── Location admin fetch ─────────────────────────────────────────────────────
  const fetchAdmin = useCallback((date) => {
    setAdminLoading(true);
    const promises = (user?.assigned_locations || []).map(locId =>
      api.get('/api/bookings/', { params: { location_id: locId, date } })
    );
    Promise.all(promises)
      .then(results => {
        // The list endpoint returns a paginated page object ({ bookings, ... }),
        // so read .data.bookings (not .data).
        const bookings = results.flatMap(r => r.data?.bookings || []);
        const active = bookings.filter(b => b.status === 'confirmed');
        // "Pending collection" = confirmed bookings whose payment isn't settled.
        const pending = active.filter(b => (b.payment?.status || 'Pending') === 'Pending');
        setAdminStats({
          bookings_count: active.length,
          pending_count: pending.length,
          cash:    active.filter(b => b.payment?.status === 'Paid' && b.payment?.method === 'Cash').reduce((s, b) => s + (b.payment?.amount || 0), 0),
          upi:     active.filter(b => b.payment?.status === 'Paid' && b.payment?.method === 'UPI').reduce((s, b) => s + (b.payment?.amount || 0), 0),
          pending_amount: pending.reduce((s, b) => s + (b.payment?.amount || 0), 0),
        });
      })
      .catch(err => {
        if (err?.response?.status !== 401 && err?.response?.status !== 403)
          toast.error('Failed to load dashboard');
      })
      .finally(() => setAdminLoading(false));
  }, [api, user?.assigned_locations]);

  // Initial load
  useEffect(() => {
    if (user?.role === 'owner') fetchOwner({});
    else fetchAdmin(adminDate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, api]);


  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleLocChange = (locId) => {
    setSelectedLoc(locId);
    const { start, end } = periodBounds(preset, customFrom, customTo);
    fetchOwner({ pStart: start, pEnd: end, locId });
  };

  const handlePresetChange = (newPreset) => {
    setPreset(newPreset);
    const { start, end } = periodBounds(newPreset, customFrom, customTo);
    fetchOwner({ pStart: start, pEnd: end, locId: selectedLoc });
  };

  const handleCustomChange = (from, to) => {
    setCustomFrom(from);
    setCustomTo(to);
    const { start, end } = periodBounds('custom', from, to);
    fetchOwner({ pStart: start, pEnd: end, locId: selectedLoc });
  };

  // ── Owner view ───────────────────────────────────────────────────────────────
  if (user?.role === 'owner') {
    const pLabel = periodLabel(preset, customFrom, customTo);

    // Build per-location chart rows from backend data + facilities list (ensures 0-value locations appear)
    const revenueMap   = Object.fromEntries((stats?.revenue_by_location  || []).map(l => [l.location_id, l.revenue]));
    const pendingMap   = Object.fromEntries((stats?.pending_by_location  || []).map(l => [l.location_id, l.pending]));
    const bookingsMap  = Object.fromEntries((stats?.bookings_by_location || []).map(l => [l.location_id, l.bookings]));

    // Stable color per location_id — built from the full facilities list so colors
    // stay fixed even when a single location is selected.
    const locationColorMap = buildLocationColorMap(facilities);

    const visibleFacs = selectedLoc ? facilities.filter(f => f.location_id === selectedLoc) : facilities;

    const locRows      = visibleFacs.map(f => ({ location_id: f.location_id, location_name: f.name, revenue:  revenueMap[f.location_id]  ?? 0 }));
    const pendingRows  = visibleFacs.map(f => ({ location_id: f.location_id, location_name: f.name, pending:  pendingMap[f.location_id]  ?? 0 }));
    const bookingsRows = visibleFacs.map(f => ({ location_id: f.location_id, location_name: f.name, bookings: bookingsMap[f.location_id] ?? 0 }));

    // Collections from backend stats (already location-filtered server-side)
    const colls = {
      cash:    stats?.today_cash    ?? 0,
      upi:     stats?.today_upi     ?? 0,
      pending: stats?.today_pending ?? 0,
    };

    // Slot utilization from backend stats (already location-filtered server-side)
    const utilMap = Object.fromEntries(
      (stats?.today_utilization || []).map(u => [`${u.location_id}-${u.ground_id}`, u])
    );
    // Build from facilities (to ensure 0-booked grounds appear), filtered by selectedLoc.
    // Fallback denominator is the ground's own active-slot count (slots vary per ground).
    const activeSlotCount = (g) => (g.slots || []).filter(s => s.active !== false).length;
    const visibleUtil = visibleFacs.flatMap(f =>
      (f.grounds || []).map(g => utilMap[`${f.location_id}-${g.id}`] || {
        location_id: f.location_id,
        location_name: f.name,
        ground_id: g.id,
        ground_name: g.name,
        booked_slots: 0,
        total_slots: activeSlotCount(g),
      })
    );

    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

    // Today's schedule + pending payments (from todayBookings). Court price comes
    // from the booking's court_price snapshot (fallback to the current slot price).
    const slotPriceMap = {};
    for (const f of facilities)
      for (const g of (f.grounds || []))
        for (const sl of (g.slots || []))
          slotPriceMap[`${f.location_id}:${g.id}:${sl.slot_code}`] = sl.price || 0;
    const calcSlotTotal = (b) => {
      const court = b.court_price != null
        ? b.court_price
        : (slotPriceMap[`${b.location_id}:${b.ground_id}:${b.slot_id}`] || 0);
      const rentals = (b.rentals || []).reduce((acc, r) => acc + (r.total || 0), 0);
      return court + rentals;
    };
    const sessionMap = {}; const sessionOrder = [];
    for (const b of todayBookings) {
      if (b.status === 'cancelled') continue;
      const k = b.session_id || `__solo__${b.booking_id}`;
      if (!sessionMap[k]) { sessionMap[k] = []; sessionOrder.push(k); }
      sessionMap[k].push(b);
    }
    const scheduleGroups = sessionOrder.map(k => sessionMap[k])
      .sort((a, b) => (a[0].slot_id || '').localeCompare(b[0].slot_id || ''));
    const pendingGroups = scheduleGroups.map(g => {
      const total = g.reduce((acc, b) => acc + calcSlotTotal(b), 0);
      const collected = g.reduce((acc, b) => acc + (parseFloat(b.payment?.amount) || 0), 0);
      return { g, total, collected, due: Math.max(0, total - collected) };
    }).filter(x => x.due > 0);

    return (
      <div>
        {/* Page header */}
        <div className="sb-page-head">
          <div>
            <div className="sb-crumb">Operations · {pLabel}</div>
            <h1>{greeting}, {user?.username || 'admin'}</h1>
            <div className="sb-sub">Overview across all facilities and time periods.</div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 22, padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12 }}>
          <LocationFilter facilities={facilities} selectedLoc={selectedLoc} onChange={handleLocChange} />
          <PeriodSelector
            preset={preset} customFrom={customFrom} customTo={customTo}
            onPresetChange={handlePresetChange} onCustomChange={handleCustomChange}
          />
        </div>

        {/* Stat grid */}
        <div className="l-grid-4" style={{ marginBottom: 14, opacity: periodLoading ? 0.5 : 1, transition: 'opacity .15s' }}>
          {globalLoading ? (
            <><SBStatCardSkeleton /><SBStatCardSkeleton /><SBStatCardSkeleton /><SBStatCardSkeleton /></>
          ) : (
            <>
              <SBStatCard label="Bookings" value={(stats?.total_bookings ?? 0).toLocaleString('en-IN')} sub={pLabel} trend={stats?.trends?.bookings} />
              <SBStatCard label="Revenue" value={(stats?.total_revenue ?? 0).toLocaleString('en-IN')} sub={pLabel} isCcy trend={stats?.trends?.revenue} />
              <SBStatCard label="Occupancy" value={stats?.occupancy ?? 0} suffix="%" sub="Today" />
              <SBStatCard label="Today's bookings" value={stats?.todays_bookings ?? '—'} sub="Real time" />
            </>
          )}
        </div>

        {/* Charts */}
        {locRows.length > 0 && (
          <div className="l-grid-3" style={{ marginBottom: 22 }}>
            <LocationBarChart title="Revenue by location" pLabel={pLabel} loading={periodLoading} data={locRows} dataKey="revenue" valueFormatter={v => `₹${v.toLocaleString('en-IN')}`} totalLabel="All total" colorMap={locationColorMap} />
            <LocationBarChart title="Pending by location" pLabel={pLabel} loading={periodLoading} data={pendingRows} dataKey="pending" valueFormatter={v => `₹${v.toLocaleString('en-IN')}`} totalLabel="All total" colorMap={locationColorMap} />
            <LocationBarChart title="Bookings by location" pLabel={pLabel} loading={periodLoading} data={bookingsRows} dataKey="bookings" valueFormatter={v => `${v}`} totalLabel="All total" colorMap={locationColorMap} />
          </div>
        )}

        {/* Today's schedule + Pending payments */}
        <div className="l-grid-2" style={{ marginBottom: 14 }}>
          {/* Today's schedule */}
          <div className="sb-card">
            <div className="sb-card-head">
              <div>
                <div className="sb-card-lbl">Today</div>
                <h3>Schedule</h3>
              </div>
              <button className="sb-btn sm" onClick={() => navigate('/admin/bookings')} style={{ textDecoration: 'none' }}>Open bookings →</button>
            </div>
            <div>
              {scheduleGroups.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>No bookings today.</div>
              ) : scheduleGroups.slice(0, 5).map(g => {
                const b0 = g[0]; const multi = g.length > 1;
                const total = g.reduce((acc, b) => acc + calcSlotTotal(b), 0);
                return (
                  <div key={b0.booking_id} onClick={() => setDrawerBooking(b0)}
                    style={{ display: 'grid', gridTemplateColumns: '56px 1fr auto', gap: 12, alignItems: 'center', padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--line-soft)' }}>
                    <div className="sb-mono" style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2 }}>
                      {(b0.slot_time || '').split(/[-–]/)[0]?.trim()}
                      {multi && <div style={{ fontSize: 10.5, color: 'var(--brand)', fontWeight: 600 }}>+{g.length - 1}</div>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <SBAvatar initials={nameInitials(b0.customer_name)} color={nameAvc(b0.customer_name)} size={28} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {b0.customer_name}{multi && <span style={{ fontSize: 10.5, color: 'var(--brand)', marginLeft: 6, fontWeight: 500 }}>{g.length} sessions</span>}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--ink-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {b0.ground_id === '__rental__' ? 'Rental' : b0.ground_id}
                        </div>
                      </div>
                    </div>
                    <div className="sb-mono sb-num" style={{ fontSize: 12.5, fontWeight: 500, textAlign: 'right' }}>
                      <span style={{ color: 'var(--ink-3)' }}>₹</span>{total.toLocaleString('en-IN')}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pending payments */}
          <div className="sb-card">
            <div className="sb-card-head">
              <div>
                <div className="sb-card-lbl">Needs attention</div>
                <h3>Pending payments</h3>
              </div>
              <span className="sb-badge warn">{pendingGroups.length} today</span>
            </div>
            <div>
              {pendingGroups.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>All today's bookings are settled ✓</div>
              ) : pendingGroups.slice(0, 4).map(({ g, total, due }) => {
                const b0 = g[0];
                return (
                  <div key={b0.booking_id} onClick={() => setDrawerBooking(b0)}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 18px', borderBottom: '1px solid var(--line-soft)', cursor: 'pointer' }}>
                    <SBAvatar initials={nameInitials(b0.customer_name)} color={nameAvc(b0.customer_name)} size={30} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b0.customer_name}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {(b0.slot_time || '').split(/[-–]/)[0]?.trim()} · {b0.ground_id === '__rental__' ? 'Rental' : b0.ground_id}
                      </div>
                    </div>
                    <div className="sb-mono sb-num" style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--warn-fg)' }}>₹{due.toLocaleString('en-IN')}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>of ₹{total.toLocaleString('en-IN')}</div>
                    </div>
                    <button className="sb-btn sm" onClick={(e) => { e.stopPropagation(); setDrawerBooking(b0); }}>Record</button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Collections + Utilization */}
        <div className="l-dash-split" style={{ opacity: periodLoading ? 0.6 : 1, transition: 'opacity .15s' }}>
          {/* Collections */}
          <div className="sb-card">
            <div className="sb-card-head">
              <div>
                <div className="sb-card-lbl">{pLabel}</div>
                <h3>Collections</h3>
              </div>
              <span className="sb-badge">{facilities.length} facilit{facilities.length === 1 ? 'y' : 'ies'}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
              {[
                { lbl: 'Cash',    val: colls.cash,              warn: false },
                { lbl: 'UPI',     val: colls.upi,               warn: false },
                { lbl: 'Pending', val: colls.pending,           warn: true },
                { lbl: 'Collected', val: colls.cash + colls.upi, warn: false },
              ].map((c, i) => (
                <div key={i} style={{ padding: '18px 16px', borderRight: i < 3 ? '1px solid var(--line-soft)' : 'none' }}>
                  <div style={{ fontSize: 10.5, color: 'var(--ink-3)', letterSpacing: '.14em', textTransform: 'uppercase', marginBottom: 6 }}>{c.lbl}</div>
                  <div className="sb-mono sb-num" style={{ fontSize: 20, fontWeight: 500, color: c.warn ? 'var(--warn-fg)' : 'var(--ink)' }}>
                    <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>₹</span>{c.val.toLocaleString('en-IN')}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Slot utilization */}
          <div className="sb-card">
            <div className="sb-card-head">
              <div>
                <div className="sb-card-lbl">Today by court</div>
                <h3>Slot utilization</h3>
              </div>
            </div>
            <div style={{ padding: '12px 18px 18px' }}>
              {visibleUtil.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>No data for this period</div>
              ) : (
                visibleUtil.map((u, i) => {
                  const pct = Math.round((u.booked_slots / u.total_slots) * 100);
                  const isLow = pct < 40;
                  return (
                    <div key={`${u.location_id}-${u.ground_id}`} style={{ display: 'grid', gridTemplateColumns: '1fr 56px 120px', gap: 12, alignItems: 'center', padding: '9px 0', borderBottom: i < visibleUtil.length - 1 ? '1px solid var(--line-soft)' : 'none' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.ground_name || u.ground_id}</div>
                        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{u.location_name}</div>
                      </div>
                      <div className="sb-mono sb-num" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{u.booked_slots}/{u.total_slots}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className={`sb-bar ${isLow ? 'warn' : ''}`} style={{ flex: 1 }}>
                          <i style={{ width: `${pct}%` }} />
                        </div>
                        <div className="sb-mono sb-num" style={{ fontSize: 11, color: 'var(--ink-2)', minWidth: 28, textAlign: 'right' }}>{pct}%</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {drawerBooking && (
          <BookingDrawer
            booking={drawerBooking}
            facilityName={facilities.find(f => f.location_id === drawerBooking.location_id)?.name}
            slotTotal={(() => {
              const g = facilities.find(f => f.location_id === drawerBooking.location_id)?.grounds?.find(x => x.id === drawerBooking.ground_id);
              const court = drawerBooking.court_price != null
                ? drawerBooking.court_price
                : (g?.slots?.find(sl => sl.slot_code === drawerBooking.slot_id)?.price || 0);
              const rentals = (drawerBooking.rentals || []).reduce((acc, r) => acc + (r.total || 0), 0);
              return court + rentals;
            })()}
            api={api}
            onClose={() => setDrawerBooking(null)}
            onMutated={() => {
              setDrawerBooking(null);
              if (selectedLoc) api.get('/api/bookings/', { params: { date: TODAY, page_size: 200, location_id: selectedLoc } }).then(r => setTodayBookings(r.data.bookings || [])).catch(() => {});
              else api.get('/api/bookings/', { params: { date: TODAY, page_size: 200 } }).then(r => setTodayBookings(r.data.bookings || [])).catch(() => {});
            }}
          />
        )}
      </div>
    );
  }

  // ── Location admin view ──────────────────────────────────────────────────────
  return (
    <div>
      <div className="sb-page-head">
        <div>
          <div className="sb-crumb">Operations</div>
          <h1>Dashboard</h1>
          <div className="sb-sub">Today's bookings at your assigned locations.</div>
        </div>
        <div className="sb-page-actions">
          <DateNav date={adminDate} onChange={d => { setAdminDate(d); fetchAdmin(d); }} />
        </div>
      </div>

      {adminLoading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          {[1, 2].map(i => <div key={i} className="sb-card" style={{ height: 132, background: 'var(--line)' }} />)}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          {/* Today's bookings → calendar */}
          <StatNavCard
            label={adminDate === TODAY ? "Today's bookings" : `Bookings · ${dateLabel(adminDate)}`}
            value={adminStats?.bookings_count ?? 0}
            hint="View on calendar →"
            onClick={() => navigate('/admin/calendar', { state: { date: adminDate } })}
          />
          {/* Pending collection → bookings filtered to pending payments for this day */}
          <StatNavCard
            label="Pending collection"
            value={adminStats?.pending_count ?? 0}
            warn
            hint="Review unpaid bookings →"
            onClick={() => navigate('/admin/bookings', { state: { date: adminDate, status: 'confirmed', payment: 'pending' } })}
          />
        </div>
      )}
    </div>
  );
}
